import React from "react";
import styles from "./LibrarySiteChooserThumbnail.module.css";
import { toUnicode } from "../utils/DomainUtils";
import { Icon, Text } from "@showitinc/showit-wc";
import UpgradedIcon from "./UpgradedIcon";
import EventUtils from "../utils/EventUtils";

const LibrarySiteChooserThumbnail = ({
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  onClick,
  siteName = "My Site",
  siteId,
  domainName,
  desktopPreview,
  overlay = null,
  highlight = false,
  selected = false,
  onToggleSelection = null,
  index,
  site,
}) => {
  const _mouseEnter = () => onMouseEnter?.();
  const _mouseLeave = () => onMouseLeave?.();

  const _createSelection = (e, forceMultiSelect = false) => {
    if (!onToggleSelection) {
      return;
    }

    const { range: rangeSelect, multi: multiSelect } =
      EventUtils.selectionType(e);
    const selection = {
      site,
      index,
      rangeSelect,
      multiSelect: forceMultiSelect || multiSelect,
      event: e.type,
    };

    onToggleSelection(selection);
  };

  /**
   * Handles the main click event for the site thumbnail.
   * Determines selection type (range/multi-select) and calls onToggleSelection if available,
   * otherwise falls back to the onClick handler.
   * @param {React.MouseEvent<HTMLDivElement>} e - The click event
   * @returns {void}
   */
  const _handleClick = (e) => {
    const target = e.target;
    if (
      target instanceof Element &&
      (target.closest(".site-chooser-thumbnail-action") ||
        target.closest(".site-chooser-thumbnail-actions"))
    ) {
      return;
    }

    _createSelection(e);
    onClick?.(e);
  };

  /**
   * Handles the checkbox select click event for the site thumbnail.
   * @param {React.MouseEvent} e - The select click event.
   */
  const _handleSelectClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    _createSelection(e, true);
  };

  let domainInfo = null;
  if (domainName != null) {
    domainInfo = (
      <div className={styles["site-chooser-thumbnail-domain"]}>
        {toUnicode(domainName)}
      </div>
    );
  }

  const isUpgraded = site?.renderer_toggle_enabled === 0;

  return (
    <div
      key={`site-design-${siteId}`}
      onClick={_handleClick}
      className={`${styles["site-chooser-thumbnail"]} ${selected ? styles["selected"] : ""}`}
      title={siteName}
      onMouseEnter={_mouseEnter}
      onMouseLeave={_mouseLeave}
    >
      {highlight && (
        <div className={styles["site-chooser-thumbnail-active-site"]}>
          Active
        </div>
      )}
      {onToggleSelection && (
        <div className={styles["site-chooser-thumbnail-actions"]}>
          <div
            className={`${styles["site-chooser-thumbnail-action"]} ${styles["site-chooser-thumbnail-select"]}`}
            onClick={_handleSelectClick}
          >
            <div className={styles["icon-background"]}>
              <Icon type="checkFill" size="20" />
            </div>
          </div>
        </div>
      )}
      <div
        className={`${styles["site-chooser-thumbnail-wrapper"]} ${
          !desktopPreview ? styles["default"] : ""
        }`}
        style={
          desktopPreview ? { backgroundImage: `url("${desktopPreview}")` } : {}
        }
      ></div>
      <div className={styles["site-chooser-thumbnail-overlay-wrapper"]}>
        {overlay}
      </div>
      <div
        className={`${styles["site-chooser-thumbnail-overlay"]} ${
          highlight ? styles["thumbnail-highlight"] : null
        }`}
      >
        <div className={styles["site-chooser-thumbnail-overlay-label"]}>
          <div className={styles["site-chooser-thumbnail-name-row"]}>
            <Text
              variant="body-small"
              className={styles["site-chooser-thumbnail-overlay-label-text"]}
            >
              {siteName}
              {isUpgraded && <UpgradedIcon className={styles.upgradedIcon} />}
            </Text>
          </div>
          {domainInfo}
        </div>
      </div>
    </div>
  );
};

export default LibrarySiteChooserThumbnail;
